import React, { useEffect, useRef } from 'react'
import { cn } from '../../lib/utils'
import { KEYBOARD_SHORTCUTS, type KeyboardShortcut } from '../../types'

export interface KeyboardShortcutsModalProps {
  isOpen: boolean
  onClose: () => void
  className?: string
}

/**
 * KeyboardShortcutsModal Component
 *
 * Displays a modal with all available keyboard shortcuts.
 * Can be opened with '?' key.
 */
export const KeyboardShortcutsModal: React.FC<
  KeyboardShortcutsModalProps
> = ({ isOpen, onClose, className }) => {
  const modalRef = useRef<HTMLDivElement>(null)

  /**
   * Trap focus within modal
   */
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      className={cn(
        'fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4',
        className
      )}
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className="bg-card rounded-xl shadow-2xl max-w-lg w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <svg
                className="w-5 h-5 text-primary-600 dark:text-primary-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-foreground">
              Keyboard Shortcuts
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close shortcuts"
          >
            <svg
              className="w-5 h-5 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Shortcuts List */}
        <div className="p-6">
          <div className="space-y-4">
            {/* Navigation Shortcuts */}
            <ShortcutSection
              title="Navigation"
              shortcuts={KEYBOARD_SHORTCUTS.filter((s) =>
                ['nextSlide', 'previousSlide', 'firstSlide', 'lastSlide', 'jumpToSlide'].includes(
                  s.action
                )
              )}
            />

            {/* View Shortcuts */}
            <ShortcutSection
              title="View Controls"
              shortcuts={KEYBOARD_SHORTCUTS.filter((s) =>
                ['toggleFullscreen', 'toggleThumbnails', 'togglePresenter'].includes(s.action)
              )}
            />

            {/* Presenter Mode Shortcuts */}
            <ShortcutSection
              title="Presenter Mode"
              shortcuts={KEYBOARD_SHORTCUTS.filter((s) =>
                s.action === 'toggleNotes'
              )}
            />

            {/* Help Shortcuts */}
            <ShortcutSection
              title="Help"
              shortcuts={KEYBOARD_SHORTCUTS.filter((s) =>
                ['showHelp', 'exit'].includes(s.action)
              )}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-muted border-t border-border">
          <p className="text-sm text-muted-foreground text-center">
            Press <kbd className="px-2 py-1 bg-card rounded border border-border font-mono text-xs">?</kbd> to toggle this help
          </p>
        </div>
      </div>
    </div>
  )
}

interface ShortcutSectionProps {
  title: string
  shortcuts: KeyboardShortcut[]
}

function ShortcutSection({ title, shortcuts }: ShortcutSectionProps) {
  if (shortcuts.length === 0) return null

  return (
    <div>
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
        {title}
      </h3>
      <div className="space-y-2">
        {shortcuts.map((shortcut) => (
          <div
            key={shortcut.action}
            className="flex items-center justify-between py-2 px-3 bg-muted rounded-lg"
          >
            <span className="text-sm text-foreground">
              {shortcut.description}
            </span>
            <kbd className="px-2 py-1 bg-card rounded border border-border font-mono text-xs text-muted-foreground">
              {shortcut.key}
            </kbd>
          </div>
        ))}
      </div>
    </div>
  )
}

export default KeyboardShortcutsModal
